// Routeplanner over het stratenraster: kortste weg (Dijkstra) over de wegen, met omwegen als de brug open
// staat of een straat is afgesloten (marktdag). De Prinsengracht is alleen over te steken via de
// ophaalbrug in de Hoofdstraat (als die dicht is) of ten zuiden van de gracht (Zuiderboulevard en lager).
// Gebruikt voor de routelijn in het spel, de deadlines en de testrijder (src/sim).

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface RouteOptions {
  /** Afgesloten stukken weg (marktdag): de route mijdt elk wegstuk dat hier doorheen loopt */
  blocked?: readonly Rect[];
  /** De ophaalbrug staat open (of gaat open): dan is de Hoofdstraat geen oversteek over de gracht */
  bridgeOpen?: boolean;
}

/** Middens van de wegen (zie CityMap.roads). */
export const ROAD_X = [420, 1200, 1920, 2740, 3520, 4320];
export const ROAD_Y = [420, 1200, 1920, 2740, 3520, 4320];

const CANAL = { x0: 2420, x1: 2540, yEnd: 2650 };
const BRIDGE_ROW = 1200; // Hoofdstraat

const side = (x: number) => (x < (CANAL.x0 + CANAL.x1) / 2 ? 'west' : 'oost');

/** Moet je van a naar b de gracht over? */
export function crossesCanal(a: Point, b: Point): boolean {
  return side(a.x) !== side(b.x);
}

const dist = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);

/** Snijdt een recht wegstuk (horizontaal of verticaal) een rechthoek? */
function hitsRect(a: Point, b: Point, r: Rect): boolean {
  return Math.min(a.x, b.x) <= r.x + r.w && Math.max(a.x, b.x) >= r.x && Math.min(a.y, b.y) <= r.y + r.h && Math.max(a.y, b.y) >= r.y;
}

/** Mag je dit wegstuk gebruiken? Niet door een afsluiting en niet door de gracht heen. */
function usable(a: Point, b: Point, opts: RouteOptions): boolean {
  for (const r of opts.blocked ?? []) if (hitsRect(a, b, r)) return false;
  const horizontal = Math.abs(a.y - b.y) < 1;
  if (horizontal && Math.min(a.x, b.x) < CANAL.x0 && Math.max(a.x, b.x) > CANAL.x1) {
    const row = a.y;
    const overBridge = Math.abs(row - BRIDGE_ROW) < 1 && !opts.bridgeOpen;
    const southOfCanal = row > CANAL.yEnd;
    if (!overBridge && !southOfCanal) return false;
  }
  return true;
}

interface Attach {
  /** Het punt op de weg waar we naartoe lopen */
  proj: Point;
  /** 'h' of 'v' en de index van de weg */
  axis: 'h' | 'v';
  line: number;
  /** Het stuk tussen twee kruispunten waar het punt op ligt */
  seg: number;
}

/** Vindt de dichtstbijzijnde weg en het stuk tussen twee kruispunten waar het punt op valt. */
function attach(p: Point): Attach {
  let best: Attach | null = null;
  let bestD = Infinity;
  ROAD_Y.forEach((y, j) => {
    const d = Math.abs(p.y - y);
    if (d < bestD) {
      bestD = d;
      best = { proj: { x: p.x, y }, axis: 'h', line: j, seg: segmentIndex(ROAD_X, p.x) };
    }
  });
  ROAD_X.forEach((x, i) => {
    const d = Math.abs(p.x - x);
    if (d < bestD) {
      bestD = d;
      best = { proj: { x, y: p.y }, axis: 'v', line: i, seg: segmentIndex(ROAD_Y, p.y) };
    }
  });
  return best!;
}

/** Tussen welke twee kruispunten ligt v? -1 = vóór het eerste, lengte-1 = na het laatste. */
function segmentIndex(list: number[], v: number): number {
  if (v <= list[0]) return -1;
  for (let i = 0; i < list.length - 1; i++) if (v <= list[i + 1]) return i;
  return list.length - 1;
}

/**
 * Plant de kortste route van a naar b. Valt er geen route te vinden (bijvoorbeeld door afsluitingen),
 * dan krijg je een rechte lijn terug: het spel loopt dan nooit vast op de planner.
 */
export function planRoute(from: Point, to: Point, opts: RouteOptions = {}): Point[] {
  // Knopen: alle kruispunten (id = i * 10 + j), plus start (100) en einde (101)
  const nodes = new Map<number, Point>();
  for (let i = 0; i < ROAD_X.length; i++) for (let j = 0; j < ROAD_Y.length; j++) nodes.set(i * 10 + j, { x: ROAD_X[i], y: ROAD_Y[j] });
  const adj = new Map<number, [number, number][]>();
  const link = (u: number, v: number) => {
    const a = nodes.get(u)!;
    const b = nodes.get(v)!;
    if (!usable(a, b, opts)) return;
    const w = dist(a, b);
    (adj.get(u) ?? adj.set(u, []).get(u)!).push([v, w]);
    (adj.get(v) ?? adj.set(v, []).get(v)!).push([u, w]);
  };
  // Wegstukken tussen aangrenzende kruispunten
  for (let i = 0; i < ROAD_X.length; i++) {
    for (let j = 0; j < ROAD_Y.length; j++) {
      if (i + 1 < ROAD_X.length) link(i * 10 + j, (i + 1) * 10 + j);
      if (j + 1 < ROAD_Y.length) link(i * 10 + j, i * 10 + j + 1);
    }
  }

  // Start en einde aan de dichtstbijzijnde weg koppelen
  const A = attach(from);
  const B = attach(to);
  for (const [id, att] of [[100, A], [101, B]] as [number, Attach][]) {
    nodes.set(id, att.proj);
    const count = att.axis === 'h' ? ROAD_X.length : ROAD_Y.length;
    // Het wegstuk tussen kruispunt `seg` en `seg + 1`; buiten het raster is er maar één buur.
    const neighbour = (k: number) => (att.axis === 'h' ? k * 10 + att.line : att.line * 10 + k);
    if (att.seg >= 0) link(id, neighbour(att.seg));
    if (att.seg + 1 < count) link(id, neighbour(att.seg + 1));
  }
  // Liggen start en einde op hetzelfde wegstuk? Dan mag je rechtstreeks.
  if (A.axis === B.axis && A.line === B.line && A.seg === B.seg) link(100, 101);

  // Dijkstra (het netwerk heeft maar ~40 knopen)
  const dst = new Map<number, number>([[100, 0]]);
  const prev = new Map<number, number>();
  const done = new Set<number>();
  while (true) {
    let u = -1;
    let best = Infinity;
    for (const [id, d] of dst) if (!done.has(id) && d < best) {
      best = d;
      u = id;
    }
    if (u < 0 || u === 101) break;
    done.add(u);
    for (const [v, w] of adj.get(u) ?? []) {
      if (done.has(v)) continue;
      const nd = best + w;
      if (nd < (dst.get(v) ?? Infinity)) {
        dst.set(v, nd);
        prev.set(v, u);
      }
    }
  }
  if (!dst.has(101)) return [from, to];

  const ids: number[] = [];
  for (let at: number | undefined = 101; at !== undefined; at = prev.get(at)) ids.unshift(at);
  const pts: Point[] = [from, ...ids.map((id) => nodes.get(id)!), to];
  // Punten die bijna samenvallen weghalen, maar houd begin en eind
  return pts.filter((p, i) => i === 0 || i === pts.length - 1 || dist(p, pts[i - 1]) > 30);
}

/** Lengte van een route in pixels. */
export const routeLength = (pts: Point[]) => pts.slice(1).reduce((s, p, i) => s + dist(p, pts[i]), 0);

/** Hoeveel pixels rijden Sjors' twee seconden inladen waard zijn. */
export const SJORS_LOAD_PX = 330;
/** Zoveel pixels moet Sjors minstens meer rijden dan jij, anders is de race niet eerlijk. */
export const RACE_MARGIN_PX = 300;

/**
 * Is een race tegen Sjors om deze klant eerlijk? Hij begint bij zijn depot en moet eerst inladen.
 * Ligt de klant vlak bij zijn depot, dan zou hij altijd winnen: dan bieden we geen race aan.
 */
export function raceIsFair(bakery: Point, depot: Point, dest: Point, opts: RouteOptions = {}): boolean {
  const you = routeLength(planRoute(bakery, dest, opts));
  const sjors = routeLength(planRoute(depot, dest, opts)) + SJORS_LOAD_PX;
  return sjors - you >= RACE_MARGIN_PX;
}

/** Pixels die een rit extra kost door een omweg, ten opzichte van de route zonder afsluitingen. */
export function detourPx(from: Point, to: Point, opts: RouteOptions): number {
  return Math.max(0, routeLength(planRoute(from, to, opts)) - routeLength(planRoute(from, to)));
}
